import React from "react";
import { MapIcon, MagnifyingGlassIcon, PlusIcon, TagIcon, WifiIcon } from "@heroicons/react/24/outline";
import TapAndPlayIcon from '@mui/icons-material/TapAndPlay';
import V2ListBoxPageSize from "./v2_listbox_page_size";

const STATUS_TABS = [
  { key: "all", label: "Tutte" },
  { key: "active", label: "Attive" },
  { key: "inactive", label: "Non attive" },
];

export default function WorkingAreaContextBar({
  search = "",
  onSearchChange,
  statusFilter = "active",
  onStatusFilterChange,
  pageSize = 25,
  onPageSizeChange,
  onAddClick,
  onManageIdentifiersClick,
  onManageNfcClick,
  totalCount = 0,
  filteredCount = 0,
  stickyClassName = "sticky top-4 z-30",
  isEmployee,
}) {
  const hasActiveFilters = search.trim() !== "" || statusFilter !== "all";

  const handleReset = () => {
    onSearchChange?.("");
    onStatusFilterChange?.("all");
  };

  return (
    <section
      className={`${stickyClassName} w-full rounded-xl border border-indigo-100 bg-white/90 shadow-sm ring-1 ring-black/5 backdrop-blur`}
    >
      {/* Barra principale */}
      <div className="px-4 py-3 md:px-5">
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-[auto_minmax(300px,1fr)_auto] lg:items-center">

          {/* Titolo e contatore */}
          <div className="flex min-w-0 items-center gap-2.5 lg:pr-2">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
              <MapIcon className="h-[27px] w-[27px] shrink-0" />
            </div>

            <div className="min-w-0">
              <p className="whitespace-nowrap text-[11px] font-medium uppercase tracking-wide text-gray-600">
                Aree di lavoro
              </p>

              <p className="mt-0.5 whitespace-nowrap text-xs text-gray-500">
                <span className="font-semibold text-gray-700">
                  {filteredCount}
                </span>{" "}
                di {totalCount}
              </p>
            </div>
          </div>

          {/* Ricerca */}
          <div className="min-w-0">
            <div className="relative w-full">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

              <input
                type="text"
                value={search}
                onChange={(event) =>
                  onSearchChange?.(event.target.value)
                }
                placeholder="Cerca per nome area..."
                className="h-10 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-700 shadow-sm outline-none placeholder:text-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Azioni */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:flex lg:items-center lg:justify-end">
            <button
              type="button"
              onClick={onAddClick}
              className="inline-flex h-9 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-indigo-600 px-3 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
            >
              <PlusIcon className="h-4 w-4 shrink-0" />
              <span className="truncate">
                Aggiungi area
              </span>
            </button>

            <button
              type="button"
              onClick={onManageIdentifiersClick}
              className="inline-flex h-9 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-indigo-200 bg-indigo-50 px-3 text-xs font-semibold text-indigo-700 shadow-sm transition-colors hover:border-indigo-300 hover:bg-indigo-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
            >
              <TagIcon className="h-4 w-4 shrink-0" />
              <span className="truncate">
                Giustificativi
              </span>
            </button>
            {!isEmployee && (
              <button
                type="button"
                onClick={onManageNfcClick}
                className="inline-flex h-9 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-indigo-200 bg-indigo-50 px-3 text-xs font-semibold text-indigo-700 shadow-sm transition-colors hover:border-indigo-300 hover:bg-indigo-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
              >
                <TapAndPlayIcon className="!h-[19px] !w-[19px]" />
                <span className="truncate">
                  Gestisci NFC
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100" />

      {/* Filtri */}
      <div className="rounded-b-xl bg-gray-50 px-4 py-2.5 md:px-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          {/* Filtri stato */}
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="mr-1 text-xs font-medium tracking-wide text-gray-500">
              Filtra:
            </span>

            {STATUS_TABS.map((tab) => {
              const isActive = statusFilter === tab.key;

              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => onStatusFilterChange?.(tab.key)}
                  className={`h-8 rounded-full border px-3 text-xs font-medium transition-colors ${
                    isActive
                      ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-100"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleReset}
                className="whitespace-nowrap text-xs font-semibold text-indigo-600 hover:text-indigo-800 hover:underline ml-2"
              >
                Reset filtri
              </button>
            )}
          </div>
          

          {/* Controlli a destra */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            <V2ListBoxPageSize
              value={pageSize}
              onChange={onPageSizeChange}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
